import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "../api/queries";
import { useAuth } from "../auth/AuthProvider";
import type { OrgListItem, VenueListItem } from "../api/types";

interface OrgContextValue {
  /** All orgs visible to the current user (FPZERO sees every org). */
  orgs: OrgListItem[];
  orgsLoading: boolean;
  /** Resolved from the current URL's `:slug` param, if any. Null if no
   *  org slug is in the URL or the slug doesn't match a visible org. */
  currentOrg: OrgListItem | null;
  /** Venues belonging to the current org (empty if no current org). */
  venues: VenueListItem[];
  venuesLoading: boolean;
  /** Resolved from the current URL's `:vslug` param, if any. */
  currentVenue: VenueListItem | null;
  refetchOrgs: () => void;
  refetchVenues: () => void;
}

const Ctx = createContext<OrgContextValue | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { slug, vslug } = useParams<{ slug?: string; vslug?: string }>();

  const orgsQuery = useQuery<OrgListItem[]>(user ? "/api/organisations" : null);
  const orgs = orgsQuery.data ?? [];

  const currentOrg = useMemo(
    () => (slug ? orgs.find((o) => o.slug === slug) ?? null : null),
    [orgs, slug],
  );

  const venuesQuery = useQuery<VenueListItem[]>(currentOrg ? `/api/venues?organisationId=${currentOrg.id}` : null);
  const venues = useMemo(
    () =>
      (venuesQuery.data ?? []).filter((v) => !currentOrg || v.organisationId === currentOrg.id),
    [venuesQuery.data, currentOrg],
  );

  const currentVenue = useMemo(
    () => (vslug ? venues.find((v) => v.slug === vslug) ?? null : null),
    [venues, vslug],
  );

  const value: OrgContextValue = {
    orgs,
    orgsLoading: orgsQuery.loading,
    currentOrg,
    venues,
    venuesLoading: venuesQuery.loading,
    currentVenue,
    refetchOrgs: orgsQuery.refetch,
    refetchVenues: venuesQuery.refetch,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrgContext(): OrgContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOrgContext must be used inside <OrgProvider>");
  return v;
}
